/**
 * searchMixin
 *
 * Provides search-as-you-type behavior shared across firmware/MIB list pages.
 *
 * ## Usage
 *
 *   import searchMixin from '../../mixins/searchMixin.js'
 *
 *   export default {
 *     mixins: [searchMixin],
 *     ...
 *   }
 *
 *   Template: bind the input's v-model to `temporarySearchQuery` and (optionally)
 *   @keydown.enter to `applySearchQuery` for instant-apply on Enter. No search
 *   button is needed — typing is what triggers a (debounced) commit.
 *
 * ## Required data properties on the host component
 *
 *   searchQuery          {String}  The committed search term used to filter and highlight.
 *   temporarySearchQuery {String}  Holds the in-progress input value bound to the
 *                                  search input's v-model.
 *
 * ## Behavior provided by the mixin
 *
 *   - A watcher on temporarySearchQuery debounces user input by SEARCH_DEBOUNCE_MS
 *     and commits to searchQuery, so callers get search-as-you-type for free.
 *   - The pending debounce timer is cancelled in beforeDestroy to avoid setting
 *     state on an unmounted component.
 *
 * ## Methods provided
 *
 *   highlightMatches(content, searchQuery, escape = true)
 *     Returns a string with all case-insensitive matches marked for highlighting.
 *     When `escape` is true (default), matches are wrapped in a yellow <span> and the
 *     surrounding content is HTML-escaped — safe to drop directly into v-html.
 *     When `escape` is false, matches are wrapped in inert sentinel tokens
 *     (HL_START / HL_END) and content is passed through unchanged — use this when the
 *     result will be fed into a markdown parser (e.g. marked via LinedTable). The
 *     downstream consumer is responsible for swapping the tokens for real <span> tags
 *     after markdown rendering (and stripping them from attributes like href).
 *     Returns the original value unchanged when searchQuery is empty.
 *
 *   applySearchQuery()
 *     Commits temporarySearchQuery → searchQuery immediately, bypassing the debounce.
 *     Bind to keydown.enter so impatient users can apply without waiting.
 */
import _ from 'lodash'

const SEARCH_DEBOUNCE_MS = 300

// Sentinel tokens used when escape=false. Chosen from the Unicode Private-Use Area
// so they cannot appear in real input or markdown syntax, yet are still ordinary
// (non-control, non-whitespace) characters that marked's inline-link regex accepts
// inside URLs without bailing out. LinedTable strips these from link attributes and
// converts them to <span> wrappers after marked renders.
export const HL_START = '\uE000'
export const HL_END = '\uE001'

function escapeHtml (str) {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default {
  created () {
    this._debouncedApplySearchQuery = _.debounce(() => {
      this.applySearchQuery()
    }, SEARCH_DEBOUNCE_MS)
  },
  beforeDestroy () {
    if (this._debouncedApplySearchQuery) {
      this._debouncedApplySearchQuery.cancel()
    }
  },
  watch: {
    temporarySearchQuery: function () {
      this._debouncedApplySearchQuery()
    }
  },
  methods: {
    highlightMatches (content, searchQuery, escape = true) {
      if (content === undefined) return content
      if (searchQuery.trim() === '') return content
      const wrap = escape ? escapeHtml : (s) => s
      const openMark = escape ? '<span style="background-color: yellow;">' : HL_START
      const closeMark = escape ? '</span>' : HL_END
      let result = ''
      let currentIndex = 0
      const text = content.toLowerCase()
      const query = searchQuery.toLowerCase()
      let matchIndex = text.indexOf(query)
      while (matchIndex !== -1) {
        result += wrap(content.slice(currentIndex, matchIndex))
        result += openMark + wrap(content.slice(matchIndex, matchIndex + query.length)) + closeMark
        currentIndex = matchIndex + query.length
        matchIndex = text.indexOf(query, currentIndex)
      }
      result += wrap(content.slice(currentIndex))
      return result
    },
    applySearchQuery () {
      this.searchQuery = this.temporarySearchQuery
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/searchMixin.js